import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { SortDescriptor } from '@progress/kendo-data-query'
import { Grid, GridColumn, GridSortChangeEvent } from '@progress/kendo-react-grid'
import { AffiliateClientDto } from 'api/Affiliate/AffiliateClientDto'
import { PageData } from 'api/Pagination/Types'
import { Pager } from 'components/components/Pager'
import { useGridState, useGridStatusState } from 'features/DataGrid'
import { CompositeFilterDescriptor, GridFilterActionState } from 'store/grid'
import { AffiliateApiClient } from 'utils/api/affiliateApi'
import { useApiClient } from 'utils/api/ApiClient'
import { buildQuery, buildSortQuery } from 'utils/QueryHelpers'
import { TableWithExport } from 'utils/table/withExport'
import { TableStateContext, TableStateContextData } from 'utils/TableStateContext'

import { affiliateClientsColumns } from './affiliateClientsColumn'

const useAffiliateClientsFetch = () => {
  const affiliateApiClient = useApiClient(AffiliateApiClient)

  const [tableData, setTableData] = useState<TableStateContextData>({ skipCount: true })
  const [affiliateClients, setAffiliateClients] = useState<AffiliateClientDto[]>([])

  const { gridStatusState, gridStatusActions } = useGridStatusState()
  const { gridState, gridActions } = useGridState()
  const { id } = useParams()

  useEffect(() => {
    fetchAffiliateClientsFetch()
  }, [])

  const fetchAffiliateClientsFetch = async () => {
    await fetchGrid(
      async (gridFilterState: GridFilterActionState): Promise<PageData<AffiliateClientDto[]>> => {
        const searchQuery = getQuery()
        const sortQuery = getSortQuery(gridFilterState.sort)
        const response = await affiliateApiClient.getAffiliateClients(id || '', {
          search: searchQuery,
          sort: sortQuery,
          pageIndex: gridState.pageIndex,
          pageSize: gridState.pageSize,
          skipCount: tableData?.skipCount !== false,
        })

        if (response?.items) {
          setAffiliateClients(response.items)
        }
        setTableData({ ...tableData, meta: response })
        gridActions.setGridFilters({
          pageSize: gridState.pageSize,
          skip: gridState.skip,
          pageIndex: gridState.pageIndex,
        })
        return response
      }
    )
  }

  const fetchGrid = async (
    callback: (gridFilterState: GridFilterActionState) => Promise<PageData<AffiliateClientDto[]>>
  ) => {
    gridStatusActions.setLoading(true)

    const response = await callback(gridState)
    if (response?.totalResults >= 0) {
      const total = response.totalResults

      gridActions.setGridFilters({
        total,
      })
    }

    gridStatusActions.setLoading(false)
  }

  const getQuery = useCallback(
    (filter?: CompositeFilterDescriptor) => {
      return buildQuery(filter ?? gridState.filter) ?? null
    },
    [gridState.filter]
  )

  const getSortQuery = useCallback(
    (sortDescriptors?: SortDescriptor[]): string | null => {
      return buildSortQuery(sortDescriptors ?? gridState.sort)
    },
    [gridState.sort]
  )

  return {
    fetchAffiliateClientsFetch,
    gridState,
    gridActions,
    tableData,
    setTableData,
    affiliateClients,
    setAffiliateClients,
    gridStatusState,
    gridStatusActions,
    getQuery,
    getSortQuery,
  }
}
export function AffiliateClientsPage() {
  const affiliateApiClient = useApiClient(AffiliateApiClient)

  const { id } = useParams()

  if (!id) {
    return
  }

  const affiliateFetch = useAffiliateClientsFetch()
  const {
    tableData,
    setTableData,
    affiliateClients,
    setAffiliateClients,
    gridState,
    gridActions,
    getQuery,
    getSortQuery,
  } = affiliateFetch

  const handleSkipCountChange = async (skipCount: boolean) => {
    const searchQuery = getQuery()
    const sortQuery = getSortQuery(gridState.sort)

    const response = await affiliateApiClient.getAffiliateClients(id, {
      search: searchQuery,
      sort: sortQuery,
      pageIndex: gridState.pageIndex,
      pageSize: gridState.take,
      skipCount,
    })
    const total = response.totalResults

    if (response?.items) {
      setAffiliateClients(response.items)
    }
    gridActions.setGridFilters({
      total,
    })
    setTableData({ ...tableData, skipCount, meta: response })
  }

  const handlePageSizeChange = async (pageSize: number) => {
    await gridActions.setGridFilters({
      ...gridState,
      pageSize,
    })

    const searchQuery = getQuery()
    const sortQuery = getSortQuery(gridState.sort)

    const response = await affiliateApiClient.getAffiliateClients(id, {
      search: searchQuery,
      sort: sortQuery,
      pageIndex: 1,
      pageSize,
      skipCount: tableData?.skipCount !== false,
    })
    await gridActions.setGridFilters({
      ...gridState,
      pageIndex: 1,
      take: pageSize,
      total: response.totalResults,
    })
    if (response?.items) {
      setAffiliateClients(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  const sortFieldMap = new Map<string, string>([['countryName.name', 'country.name']])

  const normalizeSortFields = (sort: SortDescriptor[]) => {
    return sort.map((sortItem) => {
      const normalizedSort = sortFieldMap.get(sortItem.field) || sortItem.field
      return {
        ...sortItem,
        field: normalizedSort,
      }
    })
  }

  const handleGridPageChange = async (page: number) => {
    const searchQuery = getQuery()
    const sortQuery = getSortQuery(gridState.sort)
    const pageIndex = page
    gridActions.setGridFilters({
      pageIndex,
    })

    const response = await affiliateApiClient.getAffiliateClients(id, {
      search: searchQuery,
      sort: sortQuery,
      pageIndex,
      pageSize: gridState.take,
      skipCount: tableData?.skipCount !== false,
    })
    const total = response.totalResults
    gridActions.setGridFilters({
      total,
    })

    gridActions.setGridFilters({
      total,
    })
    if (response?.items) {
      setAffiliateClients(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  const handleSortChange = async (event: GridSortChangeEvent) => {
    gridActions.setGridFilters({
      sort: event.sort,
    })
    const normalizedSort = normalizeSortFields(event.sort)

    const searchQuery = getQuery()
    const sortQuery = getSortQuery(normalizedSort)

    const response = await affiliateApiClient.getAffiliateClients(id, {
      search: searchQuery,
      sort: sortQuery,
      pageSize: gridState.take,
      skipCount: tableData?.skipCount !== false,
    })
    const total = response.totalResults
    gridActions.setGridFilters({
      total,
    })

    gridActions.setGridFilters({
      total,
    })
    if (response?.items) {
      setAffiliateClients(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  return (
    <TableStateContext.Provider value={[tableData, setTableData]}>
      <TableWithExport data={affiliateClients}>
        <Grid
          {...gridState}
          resizable
          data={affiliateClients}
          sortable
          sort={gridState.sort}
          total={gridState.total}
          take={gridState.take}
          skip={gridState.skip}
          onSortChange={handleSortChange}
          pageable
          pager={() => (
            <Pager
              onSkipCountChange={handleSkipCountChange}
              onPageChange={handleGridPageChange}
              onPageSizeChange={handlePageSizeChange}
              page={gridState.pageIndex}
              pageSize={gridState.pageSize}
              take={gridState.take || 10}
              skip={gridState.skip || 0}
            />
          )}
          style={{ zIndex: 0 }}
        >
          {affiliateClientsColumns.map((column, idx) => (
            <GridColumn key={idx + 2} {...column} />
          ))}
        </Grid>
      </TableWithExport>
    </TableStateContext.Provider>
  )
}
